import { Fragment, useCallback, useEffect, useState } from 'react';
import { fetchHawbCronDebugSnapshot, runHawbCronNow } from '../../api/debugController';
import {
  Badge,
  PanelHeader,
  StatusBanner,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
  tableHeadClassName,
} from '../../components/ui';

function formatCount(value) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatWhen(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN');
  } catch {
    return String(value);
  }
}

function durationLabel(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

function statusTone(status) {
  if (status === 'succeeded') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running') return 'warning';
  return 'default';
}

export default function HawbCronDebugPanel({ token }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [expandedRunId, setExpandedRunId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await fetchHawbCronDebugSnapshot(token);
      setSnapshot(payload);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to load HAWB cron logs.',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRunNow = async () => {
    setBusy(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const payload = await runHawbCronNow(token);
      if (payload.snapshot) setSnapshot(payload.snapshot);
      else await load();
      setStatus({
        type: 'success',
        message: payload.message || 'HAWB cron run finished.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to run HAWB cron.',
      });
    } finally {
      setBusy(false);
    }
  };

  const lastRun = snapshot?.lastRun;
  const runs = snapshot?.runs || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">HAWB cron</h2>
          <p className="crm-text-muted mt-1 text-sm">
            Auto-fills empty HAWB from UDS for unique US tracking IDs (every{' '}
            {snapshot?.intervalHours ?? 3}h). Skips trackings that already have HAWB.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={load}
            disabled={loading || busy}
          >
            Refresh
          </button>
          <button
            type="button"
            className={primaryButtonClassName}
            onClick={handleRunNow}
            disabled={loading || busy}
          >
            {busy ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {status.type !== 'idle' ? <StatusBanner status={status} setStatus={setStatus} /> : null}

      <section className={panelClassName}>
        <PanelHeader
          title="Cron status"
          subtitle="Config and current backlog of unique trackings still missing HAWB."
          action={
            <Badge tone={snapshot?.enabled ? 'success' : 'warning'}>
              {snapshot?.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          }
        />
        <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Interval" value={`${snapshot?.intervalHours ?? '—'} h`} />
          <StatCard label="Batch / run" value={formatCount(snapshot?.batchSize)} />
          <StatCard label="Delay between calls" value={`${snapshot?.delayMs ?? '—'} ms`} />
          <StatCard
            label="Pending unique trackings"
            value={formatCount(snapshot?.pendingUniqueTrackings)}
          />
          <StatCard label="Queue rows" value={formatCount(snapshot?.queue?.queued)} />
          <StatCard label="Due now" value={formatCount(snapshot?.queue?.dueNow)} />
          <StatCard
            label="Last run"
            value={lastRun ? formatWhen(lastRun.startedAt) : 'Never'}
          />
          <StatCard
            label="Last found / lines"
            value={
              lastRun
                ? `${formatCount(lastRun.hawbFound)} / ${formatCount(lastRun.linesUpdated)}`
                : '—'
            }
          />
        </div>
      </section>

      <section className={panelClassName}>
        <PanelHeader
          title="Run history"
          subtitle="Each cron or manual pass — how many trackings processed and HAWBs received."
        />
        <div className="overflow-x-auto px-5 pb-5">
          {loading && !runs.length ? (
            <p className="crm-text-muted py-8 text-center text-sm">Loading runs…</p>
          ) : !runs.length ? (
            <p className="crm-text-muted py-8 text-center text-sm">
              No HAWB cron runs logged yet.
            </p>
          ) : (
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr>
                  <th className={tableHeadClassName}>Started</th>
                  <th className={tableHeadClassName}>Source</th>
                  <th className={tableHeadClassName}>Status</th>
                  <th className={tableHeadClassName}>Pending</th>
                  <th className={tableHeadClassName}>Processed</th>
                  <th className={tableHeadClassName}>HAWB found</th>
                  <th className={tableHeadClassName}>Not ready</th>
                  <th className={tableHeadClassName}>Not found</th>
                  <th className={tableHeadClassName}>Errors</th>
                  <th className={tableHeadClassName}>Lines updated</th>
                  <th className={tableHeadClassName}>Duration</th>
                  <th className={tableHeadClassName} />
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => {
                  const open = expandedRunId === run.id;
                  return (
                    <Fragment key={run.id}>
                      <tr className="border-t border-slate-100">
                        <td className={tableCellClassName}>{formatWhen(run.startedAt)}</td>
                        <td className={tableCellClassName}>{run.triggerSource || 'cron'}</td>
                        <td className={tableCellClassName}>
                          <Badge tone={statusTone(run.status)}>{run.status}</Badge>
                        </td>
                        <td className={tableCellClassName}>
                          {formatCount(run.pendingUniqueTrackings)}
                        </td>
                        <td className={tableCellClassName}>{formatCount(run.processed)}</td>
                        <td className={tableCellClassName}>{formatCount(run.hawbFound)}</td>
                        <td className={tableCellClassName}>{formatCount(run.hawbNotReady)}</td>
                        <td className={tableCellClassName}>{formatCount(run.notFound)}</td>
                        <td className={tableCellClassName}>{formatCount(run.errors)}</td>
                        <td className={tableCellClassName}>{formatCount(run.linesUpdated)}</td>
                        <td className={tableCellClassName}>
                          {durationLabel(run.startedAt, run.finishedAt)}
                        </td>
                        <td className={tableCellClassName}>
                          <button
                            type="button"
                            className="text-xs font-semibold text-blue-600 hover:underline"
                            onClick={() =>
                              setExpandedRunId((current) => (current === run.id ? null : run.id))
                            }
                          >
                            {open ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {open ? (
                        <tr className="bg-slate-50/80">
                          <td className={tableCellClassName} colSpan={12}>
                            {run.errorMessage ? (
                              <p className="mb-2 text-xs font-medium text-red-700">
                                {run.errorMessage}
                              </p>
                            ) : null}
                            {(run.details || []).length ? (
                              <ul className="max-h-56 space-y-1 overflow-y-auto font-mono text-[11px] text-slate-700">
                                {run.details.map((detail, index) => (
                                  <li key={`${run.id}-${index}`}>
                                    [{detail.status}] {detail.tracking}
                                    {detail.batchNames?.length
                                      ? ` · batch: ${detail.batchNames.join(', ')}`
                                      : ''}
                                    {detail.hawb ? ` → ${detail.hawb}` : ''}
                                    {detail.linesUpdated != null
                                      ? ` · lines ${detail.linesUpdated}`
                                      : ''}
                                    {detail.message ? ` · ${detail.message}` : ''}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="crm-text-muted text-xs">No per-tracking details.</p>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}
